import * as _ from 'lodash'
import { Variable, all, not, VArray, VNumber } from 'alkali'
import { ElementProperties, Table, TBody, TD, TH, TR, and } from 'alkali'
import Icon from 'components/Icon'
import { FieldType, RowType, Extraction, STUDY_LEVEL_GRP } from 'services/extract/types'
import { VRow, VGroup, analysisMinGroups, analysisMaxGroups } from './model/extraction'
import { cumulativeAllOutcomes } from 'model/EvidenceSearch'
import ExtractionModel from './model/ExtractionModel'
import { FocusedState } from './ExtractAnnotationModel'
import OutcomeComponent from './OutcomeComponent'
import RowHead from './RowHead'
import {
	Mode,
	GroupsContext,
	groupRows,
	interventionCells,
	interventionOpCells,
	AddGroupRow,
	groupState, groupStatusMsg, readOnlyGroupHeaderRow
} from './GroupComponents'
import { CellCells, CellHeaders, CellInputs, CellSpacers } from './CellComponents'
import * as multipleUsers from 'icons/multiple-circle.svg'

interface GroupsTableOpts extends ElementProperties<GroupsTable> {
	row: VRow
	extractionModel: ExtractionModel
	section: string
	rowTypeTitle?: string
	focusedState: FocusedState,
	activeIndex: VNumber
}

const SpacerRow = TR.with({
	created(props) {
		props.content = [
			TD('.spacer-row'),
			TD(),
			TD(),
			TD({ classes: { hidden: not(props.showItvnOp) } }),
			TD('.group-n-cell'),
			...CellSpacers(props.row)
		]
	}
})

export default class GroupsTable extends Table('.groups-table') {
	created(props: GroupsTableOpts) {
		const ITVN_DOSAGE_COLS = 2
		const ITVN_COLS = 1 /* group row head */ + 1 /* itvn name col */ + ITVN_DOSAGE_COLS
		const { row, extractionModel, focusedState, section, rowTypeTitle = section, activeIndex } = props
		const { summary, extraction: { groups }, comparison } = extractionModel
		const type = row.type
		const ctns = type.to(t => t as unknown as FieldType == FieldType.CONTINUOUS)
		// reuse the row unit field to represent binary pct/num
		const isPctChange = row.rowType.to(r => r == RowType.PCT_CHANGE)
		const effUnit = isPctChange.to(b => b ? '%' : row.unit)
		const showItvnOp = groups.to(groups => _.some(groups, g => !g.existing && g.interventions?.length > 1))
		const itvnOpRowColSpan = showItvnOp.to(addtlCol => ITVN_COLS + +addtlCol)
		const rowCompColSpan = showItvnOp.to(addtlCol => ITVN_COLS + +addtlCol)
		//const itvnDosageOpCols = showItvnOp.to(addtlCol => ITVN_DOSAGE_COLS + +addtlCol)
		const mode = this.getMode(row, extractionModel)
		const rowComponent = this.createRowComponent(extractionModel, row, section, mode, focusedState)
		rowComponent.addEventListener('rowtype-changed', e => {
			// we are switching from continuous to binary, clear cell values
			const [fieldType, rowType] = e.selected.split('-')
			if (fieldType == FieldType.BINARY) {
				const cells = row.cells.valueOf() || {}
				const groupIds = (groups.valueOf() || []).map(g => g?.id || STUDY_LEVEL_GRP)
				for (const gid of groupIds) {
					const cell = cells[gid]
					if (cell) {
						delete cell.val
						delete cell.num
						delete cell.sd
						delete cell.pct
					}
				}
				row.cells.put(cells)
			}
		})
		const groupsContext = this.createGroupsContext(extractionModel, row, focusedState)
		const isNonCTGDocData = extractionModel.study?.study?.hasDocData && extractionModel.study?.study.metadata?.DatabaseType != 'ClinicalTrials.gov'
		props.content = groups.map((group: VGroup) => {
			const outcomeValueCells = CellInputs({ group, row, type, isPctChange, effUnit, rowComponent, focusedState })
			const state = groupState(mode, groupsContext, group)
			const renderGroupName = group.property('existing').to(existing => existing && isNonCTGDocData)
			const rowCount = all([renderGroupName, group.interventions], (existing, itvns) => renderGroupName ? 1 : itvns.length)
			const inputCells = CellCells(outcomeValueCells, rowCount)
			const leadingRow = renderGroupName.to(r =>
				// read only group header row
				r ? readOnlyGroupHeaderRow({
					groupsContext,
					group,
					inputCells,
					state,
					hideOp: not(showItvnOp)
				}) : undefined
			)
			const itvnCells = renderGroupName.to(r => r ? [] : group.interventions.map(intervention => ({
				...interventionCells(intervention, group.interventions, focusedState, not(showItvnOp), state),
				trailing: renderGroupName.to(r => (!r && group.interventions.indexOf(intervention) == 0) ? inputCells : [])
			}))).as(VArray)
			const statusMsg = groupStatusMsg(group, groups, row, extractionModel.study, comparison)
			return groupRows({ groupsContext, group, itvnCells, leadingRow }, state).to(rows =>
				TBody('.group-table-row', { classes: { 'dd-group': renderGroupName } }, [
					SpacerRow({ showItvnOp, row }),
					leadingRow,
					...rows,
					TR('.op-row', interventionOpCells(group, itvnOpRowColSpan, state, statusMsg))
				]))
		}).to(rows => {
			return all(rows)
		}).to(rows => [
			new RowHead({ row, rowComponent, rowColSpan: rowCompColSpan, activeIndex, summary, comparison, rowTypeTitle }),
			TBody([
				TR([
					TH({ attributes: { scope: 'col' }, title: 'Group' }),
					TH({ attributes: { scope: 'col' } }, 'Intervention'),
					TH('.dosage-hdr', { attributes: { scope: 'col' } }, 'Dosage'),
					TH({ attributes: { scope: 'col' }, classes: { hidden: not(showItvnOp) } }),
					TH('.group-n-hdr', { attributes: { scope: 'col' } }, 'Group N'),
					...CellHeaders(row, rowComponent, rowTypeTitle)
					/*, TH()*/])
			]),
			...rows.flat(),
			// can't use transform here or last row gets duplicated each time groups is updated
			AddGroupRow(groupsContext)
		])
	}

	protected getMode(row, model: ExtractionModel): Variable {
		return model.editingExtraction.to(e => e ? Mode.EXTRACT_EDIT : Mode.EXTRACT_ADD)
	}

	protected createRowComponent({ summary, extraction, addValidation }: ExtractionModel, row, section, mode, focusedState): any {
		return new OutcomeComponent({
			summary,
			mode,
			outcome: row,
			outcomes: extraction[section],
			allOutcomes: () => Array.from(cumulativeAllOutcomes.map.values()),
			addValidation,
			focusedState,
			extracting: true
		})
	}

	protected createGroupsContext(extractionModel: ExtractionModel, row: VRow, focusedState: FocusedState): GroupsContext {
		const { extraction: { groups,  analysisType }, editingExtraction, study: studyCtx, addValidation } = extractionModel
		const groupsAndAnalysis = all([groups, analysisType])
		const canDeleteGroup = groupsAndAnalysis.to(([groups, analysisType]) => groups.length > analysisMinGroups(analysisType))
		const allGroupSlotsTaken = all([extractionModel.extraction, extractionModel.comparison], (extraction: Extraction, comparison) => {
			const related = extraction.groups.map(group => ({ group, existing: group.existing?.id ?? comparison.groupMap[group.id]?.[0]?.Id }))
			const [existing, added] = _.partition(related, e => !!e.existing)
			const canBeAdded = _.difference(studyCtx.groups.map(g => g.Id), existing.map(e => e.existing)).length
			return canBeAdded <= added.length
		})
		const canAddGroup = groupsAndAnalysis.to(([groups, analysisType]) => groups.length < analysisMaxGroups(analysisType))
		const showAddGroupRow = and(not(editingExtraction), and(canAddGroup, studyCtx.canCreateGroups || not(allGroupSlotsTaken)))
		return {
			groups,
			studyCtx,
			addValidation,
			canAddGroup: showAddGroupRow,
			canDeleteGroup,
			invalidNewGroups: extractionModel.invalidNewGroups,
			outcome: row,
			focusedState
		}
	}
}